/**
 * The configurator's visual zone on our four breakpoints, sized after
 * Apple's buy page (apple.com/shop/buy-iphone, measured):
 *
 * - Mobile: edge to edge with square corners at 3:2 (390x260 at 390), a
 *   touch taller than Tesla's 13:7 phone frame (tesla.com/model3/design) to
 *   hold the feature tabs along its foot (MaticGallery); held as a ratio so
 *   a wider phone-sized window keeps the shape, and pinned to the top of the
 *   screen once the nav scrolls away, as Tesla's, while the options scroll
 *   underneath.
 * - Tablet: the same, at 16:9, the films' own shape, so nothing is cropped.
 *   Apple serves tablets its desktop layout; our tablet runs to 1279 and
 *   stays stacked.
 * - Stacked, it always fills the width and stays pinned. Its ratio gives
 *   way first: where the ratio would take more than 40% of the window's
 *   height (STACKED_ZONE_MAX_VIEWPORT), or 360px on a phone, it flattens to
 *   fit, so a landscape tablet keeps room for the options under it.
 * - Small Desktop and Desktop: the grid's fluid first column, pinned under
 *   the nav, the announcement and the assurance strip. Sized the way Apple's
 *   buy page sizes its gallery (apple.com/shop/buy-iphone, measured from
 *   1024x768 to 2560x1440): as high as the viewport allows less the chrome,
 *   the purchase bar (--pinned-bar-height) and the gaps, but never
 *   taller than it is wide and never past 1200px, so a tall narrow window
 *   gets a square rather than a portrait sliver.
 *
 * From `tab` it rounds its corners (`rounded-m`), after Apple's buy page.
 *
 * The pin offsets from `sd` come from the live chrome (useChromeHeights)
 * through --zone-top-side; their fallbacks are the chrome's
 * measured heights, so nothing shifts before it's measured. The frame is a
 * size container, so what it holds can contain itself below `sd` and cover
 * from `sd` up (see MaticGallery's STILL_BOX).
 *
 * Every visual the zone shows (films, clips, renders, the 3D model) takes
 * this one frame, so a swap never moves the page.
 */
/** `sd`: from here the zone sits beside the options rather than over them. */
export const SIDE_BY_SIDE_QUERY = "(min-width: 1280px)";

export const IMAGE_COLUMN_FRAME = [
  // 16px under the nav at rest at every width. Phones run edge to edge with
  // square corners; from `tab` it sits inside the margins, rounded.
  "relative z-10 mt-m -mx-grid-margin w-[calc(100%+2*var(--grid-margin))] shrink-0 overflow-hidden [container-type:size] tab:mx-0 tab:w-full tab:rounded-m",
  // Pinned at the top while stacked, options scroll up under it: a ring of
  // the page's ground keeps a 16px gap under it clear of them. Beside the
  // options from `sd` nothing passes under it, so the ring goes.
  "outline-[16px] outline-surface-page [outline-style:solid] sd:outline-0",
  "sticky top-0 h-auto max-h-[min(360px,40svh)] [aspect-ratio:3/2] tab:max-h-[40svh] tab:[aspect-ratio:16/9]",
  // From `sd` the assurance strip (ASSURANCE_STRIP_PIN) pins over it.
  "sd:z-auto sd:mt-[var(--assurance-strip-offset)] sd:top-[calc(var(--zone-top-side,172px)+var(--assurance-strip-offset))] sd:self-start d:top-[calc(var(--zone-top-side,180px)+var(--assurance-strip-offset))]",
  // Apple's rule: the width sets a square, the viewport and 1200px cap it.
  "sd:h-auto sd:[aspect-ratio:1] sd:max-h-[min(1200px,calc(100svh-var(--zone-top-side,172px)-var(--pinned-bar-height)-var(--assurance-strip-offset)-16px))] d:max-h-[min(1200px,calc(100svh-var(--zone-top-side,180px)-var(--pinned-bar-height)-var(--assurance-strip-offset)-16px))]",
].join(" ");

/**
 * The assurance strip over the zone, after Eight Sleep: in flow above it while
 * stacked (hidden on phones); from `sd` a band pinned a gap under the
 * chrome, in the same grid area, the zone held below by its top margin: the
 * --assurance-strip-* variables (globals.scss) set all three.
 */
export const ASSURANCE_STRIP_PIN =
  "order-first sd:sticky sd:order-none sd:col-start-1 sd:row-span-2 sd:row-start-1 sd:self-start sd:top-[calc(var(--zone-top-side,172px)+var(--assurance-strip-gap))] d:top-[calc(var(--zone-top-side,180px)+var(--assurance-strip-gap))]";

/**
 * The zone while pinned and stacked, for the trigger line under it: its
 * width to height (the frame's aspect ratios), the page margins it sits in,
 * and the phone's 360px cap.
 */
export const STACKED_ZONE = {
  phone: { ratio: 3 / 2, margin: 0, maxHeight: 360 },
  tablet: { ratio: 16 / 9, margin: 32, maxHeight: Number.POSITIVE_INFINITY },
} as const;

/**
 * The share of the window a pinned stacked zone may take, so a landscape
 * tablet or phone keeps room for the options under it. The frame's
 * `40svh` above writes it out.
 */
export const STACKED_ZONE_MAX_VIEWPORT = 0.4;

/**
 * Where the active block's trigger line sits beside the zone, as an
 * IntersectionObserver root margin: 40% down the viewport, with a 1% band
 * for the line. Stacked, the line sits just under the pinned zone, which
 * useActiveBlock works out in pixels.
 */
export const SIDE_BY_SIDE_TRIGGER_MARGIN = "-40% 0px -59% 0px";

/**
 * The configurator section's grid, after Tesla's design page: stacked, the
 * visual zone leads under the nav (the child takes `order-first`) and the
 * title and first model card follow close enough to show on load above the
 * pinned bar. From `sd` the zone takes a fluid first column and the options
 * the live site's fixed 420px track, 64px from it.
 */
export const VISUAL_FIRST_SECTION_LAYOUT =
  "flex flex-col items-start gap-l px-grid-margin pb-xxl pt-0 sd:grid sd:grid-cols-[minmax(0,1fr)_420px] sd:grid-rows-[auto_1fr] sd:gap-x-4xl sd:gap-y-xxl";
